import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Crown, Sparkles, Loader2, Gift, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OnboardingStep, FadeUp } from './OnboardingStep';
import { useSubscription } from '@/hooks/useSubscription';
import { useToast } from '@/hooks/use-toast';

interface StepPaywallProps {
  onComplete: (tier: 'basic' | 'pro' | 'free_for_life') => void;
  isLoading: boolean;
}

const BASIC_FEATURES = [
  'Daily task management',
  'Focus timer',
  'The ONE Thing methodology',
  '7-day task history',
  '3 custom tags',
];

const PRO_FEATURES = [
  'Everything in Basic',
  'Unlimited task history',
  'Advanced productivity insights',
  'Unlimited custom tags',
  'Weekly AI summaries',
  'Cloud sync across devices',
];

export function StepPaywall({ onComplete, isLoading }: StepPaywallProps) {
  const { startTrial, redeemCoupon } = useSubscription();
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState<'basic' | 'pro' | null>(null);
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isStartingTrial, setIsStartingTrial] = useState(false);

  const handleContinueFree = () => {
    onComplete('basic');
  };

  const handleStartTrial = async () => {
    setIsStartingTrial(true);
    const result = await startTrial();
    setIsStartingTrial(false);
    if (result.success) {
      toast({
        title: '🎉 Trial started!',
        description: 'Enjoy 7 days of Luma Pro, free.',
      });
      onComplete('pro');
    } else {
      toast({
        title: 'Error',
        description: result.error || 'Failed to start trial',
        variant: 'destructive',
      });
    }
  };

  const handleRedeemCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setIsRedeeming(true);
    const result = await redeemCoupon(couponCode);
    setIsRedeeming(false);
    
    if (result.success) {
      toast({
        title: '🎁 Coupon redeemed!',
        description: result.tier === 'free_for_life' 
          ? 'You now have Luma Pro for life!'
          : 'Your subscription has been upgraded!',
      });
      onComplete(result.tier || 'pro');
    } else {
      toast({
        title: 'Invalid coupon',
        description: result.error || 'Please check your code and try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <OnboardingStep>
      <FadeUp delay={0.1}>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
          Choose your plan
        </h1>
        <p className="text-muted-foreground mb-12">
          Start free, upgrade when you're ready.
        </p>
      </FadeUp>

      {/* Plan cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 w-full max-w-lg mb-10 px-2 sm:px-0">
        {/* Basic Plan */}
        <FadeUp delay={0.2}>
          <motion.button
            onClick={() => setSelectedPlan('basic')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`relative w-full p-5 rounded-xl border text-left transition-all ${
              selectedPlan === 'basic'
                ? 'border-primary bg-primary/5'
                : 'border-border/50 bg-card/50 hover:border-border'
            }`}
          >
            <div className="mb-4">
              <h3 className="font-semibold text-lg">Basic</h3>
              <p className="text-2xl font-bold">Free</p>
              <p className="text-xs text-muted-foreground">Forever</p>
            </div>
            
            <ul className="space-y-2">
              {BASIC_FEATURES.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </motion.button>
        </FadeUp>

        {/* Pro Plan */}
        <FadeUp delay={0.3}>
          <motion.button
            onClick={() => setSelectedPlan('pro')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`relative w-full p-5 rounded-xl border text-left transition-all ${
              selectedPlan === 'pro'
                ? 'border-primary bg-primary/5'
                : 'border-border/50 bg-card/50 hover:border-border'
            }`}
          >
            {/* Pro badge */}
            <div className="absolute -top-2 -right-2">
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                <Sparkles className="h-3 w-3" />
                Popular
              </div>
            </div>
            
            <div className="mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Crown className="h-4 w-4 text-primary" />
                Pro
              </h3>
              <p className="text-2xl font-bold">$4.99</p>
              <p className="text-xs text-muted-foreground">per month</p>
            </div>
            
            <ul className="space-y-2">
              {PRO_FEATURES.slice(0, 5).map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </motion.button>
        </FadeUp>
      </div>

      {/* Action buttons */}
      <FadeUp delay={0.4}>
        <div className="w-full max-w-lg space-y-4 mt-2">
          {selectedPlan === 'pro' ? (
            <Button
              onClick={handleStartTrial}
              disabled={isStartingTrial || isLoading}
              className="w-full h-12 text-base gap-2"
            >
              {isStartingTrial ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Start 7-day free trial
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleContinueFree}
              disabled={isLoading}
              variant={selectedPlan === 'basic' ? 'default' : 'outline'}
              className="w-full h-12 text-base"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Continue with Basic'
              )}
            </Button>
          )}

          {selectedPlan === 'pro' && (
            <p className="text-xs text-center text-muted-foreground">
              No payment required for trial. Cancel anytime.
            </p>
          )}
        </div>
      </FadeUp>

      {/* Coupon section */}
      <FadeUp delay={0.5}>
        <div className="w-full max-w-lg mt-10 pt-8 border-t border-border/50">
          {!showCoupon ? (
            <button
              onClick={() => setShowCoupon(true)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mx-auto"
            >
              <Gift className="h-4 w-4" />
              Have a coupon code?
            </button>
          ) : (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Enter coupon code</span>
                <button
                  onClick={() => setShowCoupon(false)}
                  className="p-1 hover:bg-muted rounded"
                >
                  <X className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="COUPON CODE"
                  className="flex-1 uppercase"
                  onKeyDown={(e) => e.key === 'Enter' && handleRedeemCoupon()}
                />
                <Button
                  onClick={handleRedeemCoupon}
                  disabled={!couponCode.trim() || isRedeeming}
                >
                  {isRedeeming ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Redeem'
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </FadeUp>
    </OnboardingStep>
  );
}
